import { useEffect, lazy, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, matchPath } from 'react-router-dom';
import { trackPageView, identifyUser } from './utils/analytics';
import Intercom from '@intercom/messenger-js-sdk';
import { useAuth0 } from '@auth0/auth0-react';
import { useTheme } from '@heroui/use-theme';
import { setAuth0TokenGetter } from 'utils/authUtils';
import * as Sentry from '@sentry/react';

import Callback from 'src/Pages/Callback';
import UsersApi from 'api/users';
import Header from 'components/Header';
import { ImpersonationBanner } from 'components/admin/ImpersonationBanner';
import Loader from 'src/components/Loader/Loader';
import SideMenu from 'components/SideMenu';
import Footer from 'src/components/Footer';
import { ParentGuard } from 'components/ParentGuard';
import { CurrentSessionPlayerProvider } from 'src/contexts/CurrentSessionPlayerContext';

import { Suspender } from 'src/Suspender';
import { RequiredFields } from 'components/RequiredFields';
import { AuthenticationGuard } from 'components/AuthGuard';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUser } from 'store/user/selectors';
import { setAuthState } from 'src/store/user/userSlice';
import { useHasFeatureFlag } from 'src/hooks/featureFlags';
import { AUTHED_ROUTES } from 'src/Routes';
import { SUBSCRIPTION_ACTIVE_STATUSES } from 'api/types/user';

import './App.css';

const AIAgentPanel = lazy(() => import('components/AIAgent/AIAgentPanel'));
const MobileAIAgent = lazy(() => import('components/AIAgent/MobileAIAgent'));
const Login = lazy(() => import('pages/Login'));
const Logout = lazy(() => import('pages/Logout'));
const AIPopup = lazy(() => import('pages/AIPopup'));
const CurrentSessionPlayer = lazy(() => import('components/CurrentSessionPlayer'));
const SharedSessionPage = lazy(() => import('src/Pages/SharedSession/SharedSessionPage'));
const SharedCampaignPage = lazy(() => import('src/Pages/SharedCampaign/SharedCampaignPage'));
const GoogleAuthCallback = lazy(() => import('src/Pages/GoogleAuthCallback'));
const CheckoutSuccess = lazy(() => import('src/Pages/CheckoutSuccess/CheckoutSuccess'));

const AuthedRouter = () => {
  const { isAuthenticated } = useAuth0();
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const intercomJwt = useAppSelector((state) => state.auth.intercomJwt);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const hasScribeAI = useHasFeatureFlag('scribeAi');

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Mobile navigation state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Function to show Intercom messenger
  const showIntercom = useCallback(() => {
    // Show the Intercom messenger
    if (window.Intercom) {
      window.Intercom('show');
    }
  }, []);

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    async function fetchAndSetUser() {
      try {
        const { data } = await UsersApi.fetchCurrentUser();
        const { data: flags } = await UsersApi.fetchFeatureFlags();

        dispatch(
          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: data.user,
            featureFlags: flags,
            campaignLimits: data.campaignLimits,
            intercomJwt: data.intercomJwt
          })
        );

        identifyUser(data.user.id, {
          email: data.user.email,
          subscriptionTier: data.user.stripeSubscriptionStatus
        });

        if (import.meta.env.VITE_SENTRY_DSN) {
          Sentry.setUser({
            id: data?.user?.id,
            email: data?.user?.email,
            username: data?.user?.discordUsername,
            ip_address: '{{auto}}'
          });
        }
      } catch (error) {
        console.error('Failed to fetch user, redirecting to login:', error);
        Sentry.captureException(error, {
          tags: { context: 'fetchAndSetUser' }
        });
        window.location.assign('/login');
      }
    }

    if (isAuthenticated && !user) {
      fetchAndSetUser();
    } else if (!isAuthenticated && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser(null);
    }
  }, [dispatch, isAuthenticated, user]);

  const currentRoute = Object.values(AUTHED_ROUTES).find((route) =>
    matchPath({ path: route.path, end: true }, location.pathname)
  );

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isOnPricingPage =
    !user?.stripeSubscriptionStatus ||
    !SUBSCRIPTION_ACTIVE_STATUSES.includes(user?.stripeSubscriptionStatus);

  Intercom({
    app_id: import.meta.env.VITE_INTERCOM_APP_ID ?? 'p51zo7ql',
    user_id: user?.id,
    name:
      user?.firstName ?? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ?? user?.email,
    email: user?.email,
    created_at: user?.createdAt
      ? Math.floor(new Date(user?.createdAt).getTime() / 1000)
      : undefined,
    alignment: 'left',
    // Hide the launcher (bubble) on mobile screens, if the pricing page is not open
    hide_default_launcher: isMobile && !isOnPricingPage,
    intercom_user_jwt: intercomJwt
  });

  return (
    <ParentGuard>
      <CurrentSessionPlayerProvider>
        <div className="flex flex-col h-screen">
          {isLoading ? (
            <Loader />
          ) : (
            <div className="flex flex-1 relative">
              {/* Mobile overlay */}
              {isMobileMenuOpen && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                  onClick={() => setIsMobileMenuOpen(false)}
                />
              )}

              {/* SideMenu with responsive props */}
              <SideMenu
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                onShowIntercom={showIntercom}
                isMobile={isMobile}
              />

              {/* Main content with responsive padding */}
              <div className="flex-1 flex flex-col max-h-screen transition-all duration-300 ease-in-out ps-0 lg:ps-40">
                <ImpersonationBanner />
                <Header onMobileMenuToggle={toggleMobileMenu} />
                <div className="flex-1 overflow-auto p-2 sm:p-4 pt-1 relative">
                  <Routes>
                    {Object.values(AUTHED_ROUTES).map((route) => (
                      <Route key={route.path} path={route.path} Component={route.component} />
                    ))}
                    <Route path="*" element={<Navigate to="/not-found" replace />} />
                  </Routes>

                  {/* Mobile AI Agent - Floating Button */}
                  {hasScribeAI && currentRoute?.aiEnabled && (
                    <div className="lg:hidden">
                      <Suspender>
                        <MobileAIAgent />
                      </Suspender>
                    </div>
                  )}
                </div>

                <CurrentSessionPlayer />
                <Footer />
              </div>

              {/* Desktop AI Agent Panel */}
              {hasScribeAI && currentRoute?.aiEnabled && (
                <div className="hidden lg:block">
                  <Suspender>
                    <AIAgentPanel />
                  </Suspender>
                </div>
              )}
            </div>
          )}
        </div>
      </CurrentSessionPlayerProvider>
    </ParentGuard>
  );
};

export default function App() {
  const { isLoading, getAccessTokenSilently } = useAuth0();
  useTheme('dark');

  useEffect(() => {
    setAuth0TokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  return isLoading ? (
    <Loader />
  ) : (
    <Routes>
      {/* DO NOT SUSPEND CALLBACK. CAUSES A RACE CONDITION WHERE THE TOKEN IS NOT AVAILABLE. */}
      <Route path="/callback" element={<Callback />} />
      <Route
        path="/login"
        element={
          <Suspender>
            <Login />
          </Suspender>
        }
      />
      <Route
        path="/logout"
        element={
          <Suspender>
            <Logout />
          </Suspender>
        }
      />
      {/* AI Popup Route - Bypasses main layout */}
      <Route
        path="/ai-popup"
        element={
          <AuthenticationGuard
            component={() => (
              <Suspender>
                <div className="h-screen flex flex-col">
                  <AIPopup />
                  <CurrentSessionPlayer />
                </div>
              </Suspender>
            )}
          />
        }
      />
      <Route path="/not-found" element={<h1>404 Not Found</h1>} />
      <Route
        path="/checkout-success"
        element={
          <Suspender>
            <CheckoutSuccess />
          </Suspender>
        }
      />
      <Route
        path="/required-fields"
        element={
          <Suspender>
            <RequiredFields />
          </Suspender>
        }
      />
      {/* Public shared session route - no authentication required */}
      <Route
        path="/shared/:hash"
        element={
          <Suspender>
            <SharedSessionPage />
          </Suspender>
        }
      />
      {/* Public shared campaign route - no authentication required */}
      <Route
        path="/shared-campaign/:hash"
        element={
          <Suspender>
            <SharedCampaignPage />
          </Suspender>
        }
      />
      {/* Google OAuth callback route */}
      <Route
        path="/google-auth/callback"
        element={
          <Suspender>
            <GoogleAuthCallback />
          </Suspender>
        }
      />
      <Route path="*" element={<AuthenticationGuard component={AuthedRouter} />} />
    </Routes>
  );
}
